import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Avatar,
  Input,
  CircularProgress,
  Chip,
  Tooltip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  IconButton,
  Container,
  Autocomplete,
  alpha
} from '@mui/material';
import { PhotoCamera, Google as GoogleIcon, DeleteOutline, LinkedIn, Logout } from '@mui/icons-material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingScreen from '../components/common/LoadingScreen';
import { API_URL } from '../services/apiConfig';
import { Toaster, toast } from 'react-hot-toast';

const commonTimezones = [
  // North America
  { value: 'America/New_York', label: 'New York, Washington DC (UTC-5/-4)', region: 'North America' },
  { value: 'America/Chicago', label: 'Chicago, Dallas, Houston (UTC-6/-5)', region: 'North America' },
  { value: 'America/Denver', label: 'Denver, Phoenix (UTC-7/-6)', region: 'North America' },
  { value: 'America/Los_Angeles', label: 'Los Angeles, San Francisco (UTC-8/-7)', region: 'North America' },
  { value: 'America/Anchorage', label: 'Alaska (UTC-9/-8)', region: 'North America' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (UTC-10)', region: 'North America' },
  { value: 'America/Toronto', label: 'Toronto, Montreal (UTC-5/-4)', region: 'North America' },
  { value: 'America/Vancouver', label: 'Vancouver, Seattle (UTC-8/-7)', region: 'North America' },
  { value: 'America/Mexico_City', label: 'Mexico City (UTC-6/-5)', region: 'North America' },
  { value: 'America/Guatemala', label: 'Guatemala City (UTC-6)', region: 'North America' },
  { value: 'America/Costa_Rica', label: 'San José, Costa Rica (UTC-6)', region: 'North America' },
  { value: 'America/Panama', label: 'Panama City (UTC-5)', region: 'North America' },
  
  // South America
  { value: 'America/Sao_Paulo', label: 'São Paulo, Rio de Janeiro (UTC-3)', region: 'South America' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (UTC-3)', region: 'South America' },
  { value: 'America/Santiago', label: 'Santiago, Chile (UTC-3/-4)', region: 'South America' },
  { value: 'America/Lima', label: 'Lima, Peru (UTC-5)', region: 'South America' },
  { value: 'America/Bogota', label: 'Bogotá, Colombia (UTC-5)', region: 'South America' },
  { value: 'America/Caracas', label: 'Caracas, Venezuela (UTC-4)', region: 'South America' },
  { value: 'America/La_Paz', label: 'La Paz, Bolivia (UTC-4)', region: 'South America' },
  { value: 'America/Montevideo', label: 'Montevideo, Uruguay (UTC-3)', region: 'South America' },
  { value: 'America/Asuncion', label: 'Asunción, Paraguay (UTC-3/-4)', region: 'South America' },
  { value: 'America/Guyana', label: 'Georgetown, Guyana (UTC-4)', region: 'South America' },
  
  // Europe
  { value: 'Europe/London', label: 'London, Edinburgh (UTC+0/+1)', region: 'Europe' },
  { value: 'Europe/Dublin', label: 'Dublin, Ireland (UTC+0/+1)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris, France (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin, Germany (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome, Milan (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid, Barcelona (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam, Netherlands (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Brussels', label: 'Brussels, Belgium (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Vienna', label: 'Vienna, Austria (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Zurich', label: 'Zurich, Switzerland (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Stockholm, Sweden (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Oslo', label: 'Oslo, Norway (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Copenhagen', label: 'Copenhagen, Denmark (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Helsinki', label: 'Helsinki, Finland (UTC+2/+3)', region: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Warsaw, Poland (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Prague', label: 'Prague, Czech Republic (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Budapest', label: 'Budapest, Hungary (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Bucharest', label: 'Bucharest, Romania (UTC+2/+3)', region: 'Europe' },
  { value: 'Europe/Athens', label: 'Athens, Greece (UTC+2/+3)', region: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Istanbul, Turkey (UTC+3)', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow, Russia (UTC+3)', region: 'Europe' },
  { value: 'Europe/Kiev', label: 'Kyiv, Ukraine (UTC+2/+3)', region: 'Europe' },
  { value: 'Europe/Lisbon', label: 'Lisbon, Portugal (UTC+0/+1)', region: 'Europe' },
  
  // Asia Pacific
  { value: 'Asia/Tokyo', label: 'Tokyo, Osaka (UTC+9)', region: 'Asia Pacific' },
  { value: 'Asia/Seoul', label: 'Seoul, South Korea (UTC+9)', region: 'Asia Pacific' },
  { value: 'Asia/Shanghai', label: 'Beijing, Shanghai (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Singapore', label: 'Singapore (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur, Malaysia (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Bangkok', label: 'Bangkok, Thailand (UTC+7)', region: 'Asia Pacific' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh City, Vietnam (UTC+7)', region: 'Asia Pacific' },
  { value: 'Asia/Jakarta', label: 'Jakarta, Indonesia (UTC+7)', region: 'Asia Pacific' },
  { value: 'Asia/Manila', label: 'Manila, Philippines (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Taipei', label: 'Taipei, Taiwan (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Mumbai', label: 'Mumbai, Delhi, Bangalore (UTC+5:30)', region: 'Asia Pacific' },
  { value: 'Asia/Kolkata', label: 'Kolkata, Chennai (UTC+5:30)', region: 'Asia Pacific' },
  { value: 'Asia/Karachi', label: 'Karachi, Pakistan (UTC+5)', region: 'Asia Pacific' },
  { value: 'Asia/Dhaka', label: 'Dhaka, Bangladesh (UTC+6)', region: 'Asia Pacific' },
  { value: 'Asia/Colombo', label: 'Colombo, Sri Lanka (UTC+5:30)', region: 'Asia Pacific' },
  { value: 'Asia/Kathmandu', label: 'Kathmandu, Nepal (UTC+5:45)', region: 'Asia Pacific' },
  { value: 'Asia/Dubai', label: 'Dubai, UAE (UTC+4)', region: 'Asia Pacific' },
  { value: 'Asia/Qatar', label: 'Doha, Qatar (UTC+3)', region: 'Asia Pacific' },
  { value: 'Asia/Kuwait', label: 'Kuwait City (UTC+3)', region: 'Asia Pacific' },
  { value: 'Asia/Riyadh', label: 'Riyadh, Saudi Arabia (UTC+3)', region: 'Asia Pacific' },
  { value: 'Asia/Tehran', label: 'Tehran, Iran (UTC+3:30/+4:30)', region: 'Asia Pacific' },
  { value: 'Asia/Baghdad', label: 'Baghdad, Iraq (UTC+3)', region: 'Asia Pacific' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem, Israel (UTC+2/+3)', region: 'Asia Pacific' },
  { value: 'Asia/Beirut', label: 'Beirut, Lebanon (UTC+2/+3)', region: 'Asia Pacific' },
  { value: 'Asia/Damascus', label: 'Damascus, Syria (UTC+2/+3)', region: 'Asia Pacific' },
  { value: 'Asia/Amman', label: 'Amman, Jordan (UTC+2/+3)', region: 'Asia Pacific' },
  
  // Australia & Oceania
  { value: 'Australia/Sydney', label: 'Sydney, Canberra (UTC+10/+11)', region: 'Australia & Oceania' },
  { value: 'Australia/Melbourne', label: 'Melbourne (UTC+10/+11)', region: 'Australia & Oceania' },
  { value: 'Australia/Brisbane', label: 'Brisbane (UTC+10)', region: 'Australia & Oceania' },
  { value: 'Australia/Perth', label: 'Perth (UTC+8)', region: 'Australia & Oceania' },
  { value: 'Australia/Adelaide', label: 'Adelaide (UTC+9:30/+10:30)', region: 'Australia & Oceania' },
  { value: 'Australia/Darwin', label: 'Darwin (UTC+9:30)', region: 'Australia & Oceania' },
  { value: 'Pacific/Auckland', label: 'Auckland, New Zealand (UTC+12/+13)', region: 'Australia & Oceania' },
  { value: 'Pacific/Fiji', label: 'Suva, Fiji (UTC+12/+13)', region: 'Australia & Oceania' },
  { value: 'Pacific/Tahiti', label: 'Tahiti (UTC-10)', region: 'Australia & Oceania' },
  { value: 'Pacific/Guam', label: 'Guam (UTC+10)', region: 'Australia & Oceania' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'Cairo, Egypt (UTC+2)', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'Lagos, Nigeria (UTC+1)', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg, Cape Town (UTC+2)', region: 'Africa' },
  { value: 'Africa/Nairobi', label: 'Nairobi, Kenya (UTC+3)', region: 'Africa' },
  { value: 'Africa/Addis_Ababa', label: 'Addis Ababa, Ethiopia (UTC+3)', region: 'Africa' },
  { value: 'Africa/Dar_es_Salaam', label: 'Dar es Salaam, Tanzania (UTC+3)', region: 'Africa' },
  { value: 'Africa/Kampala', label: 'Kampala, Uganda (UTC+3)', region: 'Africa' },
  { value: 'Africa/Kigali', label: 'Kigali, Rwanda (UTC+2)', region: 'Africa' },
  { value: 'Africa/Accra', label: 'Accra, Ghana (UTC+0)', region: 'Africa' },
  { value: 'Africa/Abidjan', label: 'Abidjan, Ivory Coast (UTC+0)', region: 'Africa' },
  { value: 'Africa/Dakar', label: 'Dakar, Senegal (UTC+0)', region: 'Africa' },
  { value: 'Africa/Casablanca', label: 'Casablanca, Morocco (UTC+0/+1)', region: 'Africa' },
  { value: 'Africa/Tunis', label: 'Tunis, Tunisia (UTC+1)', region: 'Africa' },
  { value: 'Africa/Algiers', label: 'Algiers, Algeria (UTC+1)', region: 'Africa' },
  { value: 'Africa/Tripoli', label: 'Tripoli, Libya (UTC+2)', region: 'Africa' },
  { value: 'Africa/Khartoum', label: 'Khartoum, Sudan (UTC+2)', region: 'Africa' },
  { value: 'Africa/Kinshasa', label: 'Kinshasa, DR Congo (UTC+1)', region: 'Africa' },
  { value: 'Africa/Luanda', label: 'Luanda, Angola (UTC+1)', region: 'Africa' },
  { value: 'Africa/Maputo', label: 'Maputo, Mozambique (UTC+2)', region: 'Africa' },
  { value: 'Africa/Harare', label: 'Harare, Zimbabwe (UTC+2)', region: 'Africa' },
  { value: 'Africa/Lusaka', label: 'Lusaka, Zambia (UTC+2)', region: 'Africa' },
  { value: 'Indian/Mauritius', label: 'Port Louis, Mauritius (UTC+4)', region: 'Africa' },
];

const EditProfile = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    headline: '',
    industry: '',
    professionalRole: '',
    targetAudience: '',
    linkedinProfile: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [skillLimitError, setSkillLimitError] = useState(false);
  const [customSkillInput, setCustomSkillInput] = useState('');
  const [userAddedSkills, setUserAddedSkills] = useState([]);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [profDetailsLoading, setProfDetailsLoading] = useState(false);
  const [profDetailsSuccess, setProfDetailsSuccess] = useState('');
  const [linkedinProfileLoading, setLinkedinProfileLoading] = useState(false);
  const [linkedinProfileSuccess, setLinkedinProfileSuccess] = useState('');
  const [linkedinProfileData, setLinkedinProfileData] = useState(null);
  const [fetchingLinkedinProfile, setFetchingLinkedinProfile] = useState(false);
  const [profileDataFetched, setProfileDataFetched] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [removingLinkedinProfile, setRemovingLinkedinProfile] = useState(false);
  const [linkedInStatus, setLinkedInStatus] = useState(false);
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [userData, setUserData] = useState(null);

  const checkGoogleConnection = useCallback(() => {
    // Check for Google ID in all possible locations in the user object
    const hasGoogleId = Boolean(
      user?.google_id || 
      user?.user?.google_id || 
      userData?.google_id
    );
    
    console.log('Google connection check:', {
      hasNestedUser: !!user?.user,
      topLevelGoogleId: user?.google_id,
      nestedGoogleId: user?.user?.google_id,
      responseDataGoogleId: userData?.google_id,
      isConnected: hasGoogleId
    });
    
    return hasGoogleId;
  }, [user, userData]);

  const profileImage = user?.user?.image || user?.image || '/default-avatar.png';
  
  // Update the useEffect that checks LinkedIn status
  useEffect(() => {
    const checkLinkedInStatus = async () => {
      try {
        console.log('=== Checking LinkedIn Status ===');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/linkedin-status`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        console.log('LinkedIn Status Response:', {
          status: response.status,
          data: response.data,
          isConnected: response.data.connected
        });
        setLinkedInStatus(response.data.connected);
        
        // If LinkedIn is connected, fetch the profile data
        if (response.data.connected) {
          fetchLinkedInProfileData();
        }
      } catch (error) {
        console.error('LinkedIn Status Error:', error);
        setLinkedInStatus(false);
      }
    };
    
    checkLinkedInStatus();
  }, [user, success]);

  // Use the state variable for LinkedIn connection status
  const isLinkedInConnected = linkedInStatus;

  useEffect(() => {
    const fetchUserDetails = async () => {
      setPageLoading(true);
      setInitialLoad(false);
      
      // Check for LinkedIn connection results in URL params
      const linkedinParam = searchParams.get('linkedin');
      const linkedinError = searchParams.get('error');
      
      if (linkedinParam === 'connected') {
        // Clear all existing LinkedIn data when connecting new account
        setLinkedinProfileData(null);
        setProfileDataFetched(false);
        localStorage.removeItem('linkedin_profile_data');
        localStorage.removeItem('linkedinBannerData');
        localStorage.removeItem('linkedinAboutData');
        localStorage.removeItem('linkedinMetricsData');
        localStorage.removeItem('linkedinBannerAnalysis');
        localStorage.removeItem('linkedinAboutAnalysis');
        localStorage.removeItem('linkedinProfilePicAnalysis');
        localStorage.removeItem('linkedinBannerTimestamp');
        localStorage.removeItem('linkedinAboutTimestamp');
        localStorage.removeItem('linkedinMetricsTimestamp');
        
        setSuccess('LinkedIn account connected successfully');
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } else if (linkedinError?.startsWith('linkedin_')) {
        setError('Failed to connect LinkedIn account. Please try again.');
      }
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const userData = response.data.user;
        
        // Update Google connection status based on API response
        setIsGoogleConnected(Boolean(userData.google_id));
        
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          headline: userData.headline || '',
          industry: userData.industry || '',
          professionalRole: userData.professionalRole || '',
          targetAudience: userData.targetAudience || '',
          linkedinProfile: userData.linkedinProfile || '',
          timezone: userData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
        });
        
        // Log LinkedIn connection status for debugging
        console.log('LinkedIn connection status:', {
          userStructure: {
            hasNestedUser: !!user?.user,
            topLevelLinkedInId: user?.linkedin_id,
            nestedLinkedInId: user?.user?.linkedin_id,
          },
          isLinkedInConnected,
          responseData: {
            hasLinkedInId: !!userData?.linkedin_id
          }
        });
        
        // Check if we have cached LinkedIn profile data in localStorage
        const cachedProfileData = localStorage.getItem('linkedin_profile_data');
        
        if (cachedProfileData) {
          try {
            // Use cached data if available
            const parsedData = JSON.parse(cachedProfileData);
            setLinkedinProfileData(parsedData);
            setProfileDataFetched(true);
            console.log('Using cached LinkedIn profile data');
          } catch (e) {
            console.error('Error parsing cached LinkedIn profile data:', e);
            localStorage.removeItem('linkedin_profile_data');
          }
        }
        
        // Only fetch LinkedIn profile data if:
        // 1. We have a LinkedIn profile URL
        // 2. We don't have cached data
        // 3. We haven't already fetched the data in this session
        if (userData.linkedinProfile && (!cachedProfileData || linkedinParam === 'connected')) {
          console.log('Fetching fresh LinkedIn data');
          fetchLinkedInProfileData(userData.linkedinProfile);
        }
        
        // Update the userData state
        setUserData(userData);
        
        // Fetch user skills
        fetchUserSkills();
        
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError('Failed to load user profile');
      } finally {
        setPageLoading(false);
      }
    };

    fetchUserDetails();
  }, [searchParams, user, isLinkedInConnected, profileDataFetched]);

  const fetchLinkedInProfileData = async () => {
    try {
      console.log('Fetching LinkedIn profile data...');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/linkedin/profile-data`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('LinkedIn profile data response:', response.data);
      
      if (response.data) {
        setLinkedinProfileData(response.data);
        setProfileDataFetched(true);
      }
    } catch (err) {
      console.error('Error fetching LinkedIn profile data:', {
        status: err.response?.status,
        message: err.response?.data?.message || err.message,
        data: err.response?.data
      });
      
      // Only set error if it's not a 404 (which is expected when no profile is found)
      if (err.response?.status !== 404) {
        setError('Failed to fetch LinkedIn profile data');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Prevent changes to Google-managed fields if connected
    if (checkGoogleConnection() && (name === 'name' || name === 'email')) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSkillSelect = (skill) => {
    setSkillLimitError(false);
    setSelectedSkills(prev => {
      if (prev.includes(skill)) {
        return prev.filter(s => s !== skill);
      }
      if (prev.length >= 5) {
        setSkillLimitError(true);
        return prev;
      }
      return [...prev, skill];
    });
  };

  const handleAddCustomSkill = () => {
    setSkillLimitError(false);
    const newSkill = customSkillInput.trim();
    if (newSkill && !userAddedSkills.includes(newSkill)) {
      setUserAddedSkills(prev => [...prev, newSkill]);
      if (selectedSkills.length < 5 && !selectedSkills.includes(newSkill)) {
        setSelectedSkills(prev => [...prev, newSkill]);
      } else if (selectedSkills.length >= 5 && !selectedSkills.includes(newSkill)) {
        setSkillLimitError(true);
      }
    }
    setCustomSkillInput('');
  };

  const handleSaveSkills = async () => {
    if (selectedSkills.length === 0) {
      setError('Please select at least one skill.');
      return;
    }
    if (selectedSkills.length > 5) {
      setError('You can select a maximum of 5 skills. Please deselect some.');
      return;
    }
    setProfDetailsLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/save-selected-skills`,
        { selectedSkills },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setProfDetailsSuccess('Skills updated successfully');
      setTimeout(() => setProfDetailsSuccess(''), 3000);
    } catch (error) {
      setError('Error saving skills');
    } finally {
      setProfDetailsLoading(false);
    }
  };

  const fetchUserSkills = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/auth/user-generation-settings`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data) {
        const skills = response.data.skills || [];
        // Parse skills if they're stored as a JSON string
        setSelectedSkills(typeof skills === 'string' ? JSON.parse(skills) : skills);
      }
    } catch (error) {
      console.error('Error fetching skills:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Ensure no undefined values are sent to the server
      const name = formData.name || null;
      const headline = formData.headline || null;
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/auth/profile`,
        {
          name,
          headline
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        setSuccess('Profile updated successfully');
        
        // Update the user context with the returned user data
        if (updateUser) {
          updateUser({
            ...user,
            ...response.data.user
          });
        }
        
        // Update localStorage
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser) {
          localStorage.setItem('user', JSON.stringify({
            ...storedUser,
            name: response.data.user.name,
            headline: response.data.user.headline
          }));
        }
        
        // Show success message for 3 seconds
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setUploadLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/profile/picture`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      await updateUser(response.data.user);
      setSuccess('Profile picture updated successfully');
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Error uploading profile picture'
      );
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/auth/account`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      console.log('Delete account response:', response.data);
      setDeleteDialogOpen(false);
      
      // Show success alert
      setShowSuccessAlert(true);
      
      // Clear user data
      localStorage.clear();
      
      // Redirect after delay
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);

    } catch (error) {
      console.error('Delete account error:', error);
      setError(error.response?.data?.message || 'Failed to delete account');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleProfessionalDetailsSubmit = async (e) => {
    e.preventDefault();
    setProfDetailsLoading(true);
    setError('');
    setProfDetailsSuccess('');

    try {
      const professionalData = {
        industry: formData.industry,
        professionalRole: formData.professionalRole,
        targetAudience: formData.targetAudience,
        linkedinProfile: formData.linkedinProfile,
        timezone: formData.timezone
      };

      // First save professional details
      const profResponse = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/auth/profile/professional`,
        professionalData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Then save skills
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/save-selected-skills`,
        { selectedSkills },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // Update the user data in localStorage and context
      const updatedUser = {
        ...user,
        user: {
          ...user.user,
          ...profResponse.data.user
        }
      };
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      if (typeof updateUser === 'function') {
        updateUser(updatedUser);
      }

      setProfDetailsSuccess('Professional details and skills updated successfully');
    } catch (err) {
      console.error('Professional details update error:', err);
      setError(
        err.response?.data?.message || 
        'An error occurred while updating your professional details'
      );
    } finally {
      setProfDetailsLoading(false);
    }
  };

  const handleSaveProfessionalDetails = async () => {
    setProfDetailsLoading(true);
    setProfDetailsSuccess('');
    
    try {
      const professionalData = {
        industry: formData.industry,
        professionalRole: formData.professionalRole,
        targetAudience: formData.targetAudience,
        linkedinProfile: formData.linkedinProfile,
        timezone: formData.timezone
      };
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/auth/profile/professional`,
        professionalData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Update the user data in context if needed
      if (response.data.success) {
        setProfDetailsSuccess('Professional details updated successfully');
        
        // If you're updating the user context, make sure to include the new field
        if (updateUser) {
          updateUser({
            ...user,
            industry: formData.industry,
            professionalRole: formData.professionalRole,
            targetAudience: formData.targetAudience,
            linkedinProfile: formData.linkedinProfile,
            timezone: formData.timezone
          });
        }
      }
    } catch (err) {
      console.error('Error updating professional details:', err);
      setError('Failed to update professional details');
    } finally {
      setProfDetailsLoading(false);
    }
  };

  const handleSaveLinkedInProfile = async () => {
    setLinkedinProfileLoading(true);
    setLinkedinProfileSuccess('');
    setError('');
    
    try {
      // First save the LinkedIn profile URL to our database
      const saveResponse = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/auth/profile/professional`,
        {
          industry: formData.industry,
          professionalRole: formData.professionalRole,
          targetAudience: formData.targetAudience,
          linkedinProfile: formData.linkedinProfile,
          timezone: formData.timezone
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (saveResponse.data.success) {
        setLinkedinProfileSuccess('LinkedIn profile updated successfully');
        
        // Only fetch the LinkedIn profile data if:
        // 1. We have a LinkedIn profile URL
        // 2. We don't already have the data
        // 3. We haven't already fetched the data in this session
        if (formData.linkedinProfile && !linkedinProfileData && !profileDataFetched) {
          setFetchingLinkedinProfile(true);
          try {
            const profileResponse = await axios.post(
              `${process.env.REACT_APP_API_URL}/api/linkedin/profile-data`,
              { profileUrl: formData.linkedinProfile },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            if (profileResponse.data) {
              // Save profile data to state
              setLinkedinProfileData(profileResponse.data);
              
              // Also save to localStorage to avoid refetching
              localStorage.setItem('linkedin_profile_data', JSON.stringify(profileResponse.data));
              
              // Update our tracking state
              setProfileDataFetched(true);
              
              console.log('LinkedIn profile data saved to localStorage');
            }
          } catch (profileErr) {
            console.error('Error fetching LinkedIn profile data:', profileErr);
          } finally {
            setFetchingLinkedinProfile(false);
          }
        }
      }
    } catch (err) {
      console.error('Error updating LinkedIn profile:', err);
      setError('Failed to update LinkedIn profile');
    } finally {
      setLinkedinProfileLoading(false);
    }
  };

  const handleRemoveLinkedInProfile = async () => {
    setRemovingLinkedinProfile(true);
    setError('');
    setLinkedinProfileSuccess('');

    try {
      // Update professional details on the backend with an empty linkedinProfile
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/auth/profile/professional`,
        {
          industry: formData.industry,
          professionalRole: formData.professionalRole,
          targetAudience: formData.targetAudience,
          linkedinProfile: '',
          timezone: formData.timezone,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.success) {
        // Clear local state and storage only after successful backend update
        setLinkedinProfileData(null);
        setProfileDataFetched(false);
        localStorage.removeItem('linkedin_profile_data');
        setFormData((prev) => ({ ...prev, linkedinProfile: '' }));

        // Optionally update user context if needed, though useEffect might handle it
        if (updateUser) {
            const updatedUserData = {
                ...user,
                linkedinProfile: '',
                // If user has nested structure:
                user: {
                    ...user?.user,
                    linkedinProfile: '',
                }
            };
            // Filter out undefined user key if it doesn't exist
            if (!user?.user) delete updatedUserData.user;
            updateUser(updatedUserData);
        }

        setLinkedinProfileSuccess('LinkedIn profile removed successfully.');
        // Clear success message after a delay
        setTimeout(() => setLinkedinProfileSuccess(''), 3000);

      } else {
        throw new Error(response.data.message || 'Failed to remove LinkedIn profile on server.');
      }
    } catch (err) {
      console.error('Error removing LinkedIn profile:', err);
      setError(err.response?.data?.message || 'Failed to remove LinkedIn profile. Please try again.');
    } finally {
      setRemovingLinkedinProfile(false);
    }
  };

  // Add the LinkedIn auth handler similar to the LinkedInSignIn component
  const handleLinkedInConnect = useCallback(() => {
    const clientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
    
    if (!clientId) {
      console.error('LinkedIn client ID not configured');
      setError('LinkedIn integration is not properly configured. Please try again later.');
      return;
    }
    
    // Use environment-specific redirect URI
    let redirectUri = process.env.REACT_APP_LINKEDIN_REDIRECT_URI;
    
    if (process.env.NODE_ENV === 'development') {
      const domain = process.env.REACT_APP_DOMAIN || 'http://localhost:3000';
      redirectUri = `${domain}/linkedin-callback`;
    }
    
    console.log('LinkedIn connection attempt:', {
      clientId,
      redirectUri,
      environment: process.env.NODE_ENV
    });
    
    // Generate state
    const state = Math.random().toString(36).substring(2, 8);
    
    // Store state
    sessionStorage.setItem('linkedin_auth_state', state);
    localStorage.setItem('linkedin_auth_state', state);
    console.log('Stored LinkedIn auth state:', state);
    
    // Clear previous errors
    localStorage.removeItem('linkedin_last_error');
    
    // Set scopes
    const scopes = [
      'openid',
      'profile',
      'email',
      'w_member_social',
      'r_basicprofile'
    ];
    const scope = encodeURIComponent(scopes.join(' '));
    
    // Build auth URL
    const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?` + 
      `response_type=code` +
      `&client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${scope}` +
      `&state=${state}`;
    
    console.log('LinkedIn authorization URL:', linkedInAuthUrl);
    window.location.href = linkedInAuthUrl;
  }, [setError]);

  // Update the disconnect handler with more debugging
  const handleDisconnectLinkedIn = async () => {
    setRemovingLinkedinProfile(true);
    setError('');
    setLinkedinProfileSuccess('');

    try {
      // Call the backend to disconnect LinkedIn
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/disconnect-linkedin`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        // Clear ALL LinkedIn-related data
        setLinkedInStatus(false);
        setLinkedinProfileData(null);
        setProfileDataFetched(false);
        
        // Clear ALL LinkedIn-related localStorage items
        localStorage.removeItem('linkedin_profile_data');
        localStorage.removeItem('linkedinBannerData');
        localStorage.removeItem('linkedinAboutData');
        localStorage.removeItem('linkedinMetricsData');
        localStorage.removeItem('linkedinBannerAnalysis');
        localStorage.removeItem('linkedinAboutAnalysis');
        localStorage.removeItem('linkedinProfilePicAnalysis');
        localStorage.removeItem('linkedinBannerTimestamp');
        localStorage.removeItem('linkedinAboutTimestamp');
        localStorage.removeItem('linkedinMetricsTimestamp');

        // Update user context
        if (updateUser) {
          const updatedUserData = {
            ...user,
            linkedin_id: null,
            linkedin_access_token: null,
            linkedinProfile: null
          };
          updateUser(updatedUserData);
        }

        // Update form data
        setFormData(prev => ({
          ...prev,
          linkedinProfile: ''
        }));

        // Update professional details in backend
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/auth/profile/professional`,
          {
            ...formData,
            linkedinProfile: null,
            timezone: formData.timezone
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        setLinkedinProfileSuccess('LinkedIn account disconnected successfully.');
        setTimeout(() => setLinkedinProfileSuccess(''), 3000);
      } else {
        throw new Error(response.data.message || 'Failed to disconnect LinkedIn account.');
      }
    } catch (err) {
      console.error('Error disconnecting LinkedIn account:', err);
      setError(err.response?.data?.message || 'Failed to disconnect LinkedIn account. Please try again.');
    } finally {
      setRemovingLinkedinProfile(false);
    }
  };

  // Modify the loading check to include all necessary states
  const isLoading = 
    initialLoad || 
    pageLoading || 
    fetchingLinkedinProfile ||
    loading ||
    uploadLoading ||
    profDetailsLoading ||
    linkedinProfileLoading ||
    removingLinkedinProfile ||
    !userData;

  // Keep the existing loading screen check
  if (isLoading) {
    return <LoadingScreen message="Loading profile..." />;
  }

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  return (
    <Container maxWidth="xl" className="page-container">
      <Helmet>
        <title>Profile Settings | Instation</title>
        <meta name="description" content="Manage your profile settings and preferences for Instation." />
      </Helmet>
      <Toaster position="top-right" reverseOrder={false} />
      
      <Box className="content-container">
        <Grid container spacing={3}>
          <Grid item xs={12} className="content-panel">
            <Paper elevation={0} className="content-paper">
              {/* Success Alert */}
              {showSuccessAlert && (
                <Alert 
                  severity="success" 
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1000,
                    minWidth: 300,
                    boxShadow: 3
                  }}
                >
                  Account deleted successfully. Redirecting...
                </Alert>
              )}
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                justifyContent: 'center',  // Center the columns
                maxWidth: '1200px',        // Set a max width
                mx: 'auto'                 // Center the container
              }}>
                {/* Left column - Basic Profile */}
                <Box sx={{ 
                  flex: { xs: '1 1 100%', md: '0 0 500px' },
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <Paper sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Profile Header */}
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 4
                    }}>
                      {/* Avatar section */}
                      <Box sx={{ position: 'relative', mb: 2 }}>
                        {uploadLoading ? (
                          <Skeleton variant="circular" width={100} height={100} />
                        ) : (
                          <Avatar
                            src={profileImage}
                            alt={formData.name || 'Profile'}
                            sx={{ width: 100, height: 100 }}
                          />
                        )}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                          accept="image/*"
                        />
                        <IconButton
                          onClick={() => fileInputRef.current.click()}
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            '&:hover': { bgcolor: 'background.paper' }
                          }}
                        >
                          <PhotoCamera />
                        </IconButton>
                      </Box>

                      {/* Form Fields and Danger Zone container */}
                      <Box sx={{ 
                        width: '100%',
                        maxWidth: '400px',
                        mx: 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        {/* Form Fields */}
                        <TextField
                          fullWidth
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          disabled={checkGoogleConnection()}
                          InputProps={{
                            sx: checkGoogleConnection() ? {
                              bgcolor: 'rgba(0, 0, 0, 0.03)'
                            } : {}
                          }}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          value={formData.email}
                          disabled
                          InputProps={{
                            sx: {
                              bgcolor: 'rgba(0, 0, 0, 0.03)'
                            }
                          }}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          label="Professional Headline"
                          name="headline"
                          value={formData.headline}
                          onChange={handleChange}
                          placeholder="e.g., Senior Software Engineer at Tech Company"
                          margin="normal"
                          variant="outlined"
                          sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.1)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#4890fe',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#4890fe',
                              }
                            }
                          }}
                        />
                        
                        {/* Google Connection */}
                        <Box sx={{ 
                          mb: 3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          borderRadius: 2,
                          bgcolor: isGoogleConnected ? 'rgba(56, 161, 105, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                          border: '1px solid',
                          borderColor: isGoogleConnected ? 'rgba(56, 161, 105, 0.2)' : 'rgba(0, 0, 0, 0.08)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GoogleIcon sx={{ color: isGoogleConnected ? '#38A169' : '#718096' }} />
                            <Typography>Google Account</Typography>
                          </Box>
                          <Chip
                            label={isGoogleConnected ? "Connected" : "Not Connected"}
                            color={isGoogleConnected ? "success" : "default"}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              ...(isGoogleConnected && {
                                bgcolor: 'rgba(56, 161, 105, 0.1)',
                                color: '#38A169'
                              })
                            }}
                          />
                        </Box>

                        {/* LinkedIn Profile Card */}
                        <Box sx={{ mb: 3 }}>
                          {!isLinkedInConnected ? (
                        <Box sx={{ 
                          p: 2,
                          borderRadius: 2,
                              bgcolor: 'rgba(0, 0, 0, 0.03)',
                              border: '1px solid rgba(0, 0, 0, 0.08)',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinkedInIcon sx={{ color: '#718096' }} />
                            <Typography>LinkedIn Account</Typography>
                          </Box>
                              
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                Connect your LinkedIn account to enable automatic posting and profile synchronization.
                              </Typography>
                              
                            <Button
                                fullWidth
                              variant="outlined"
                              onClick={handleLinkedInConnect}
                                startIcon={<LinkedInIcon />}
                              sx={{
                                  borderColor: '#0A66C2',
                                  color: '#0A66C2',
                            borderRadius: '8px',
                                  py: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                    borderColor: '#084482',
                                    bgcolor: 'rgba(10, 102, 194, 0.04)'
                            }
                          }}
                        >
                                Connect LinkedIn Account
                        </Button>
                            </Box>
                          ) : (
                        <Box sx={{ 
                              border: '1px solid rgba(0, 0, 0, 0.08)',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              bgcolor: 'rgba(14, 118, 168, 0.05)'
                            }}>
                              <Box sx={{ position: 'relative', px: 2, pt: 2, pb: 2, bgcolor: '#ffffff', display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                  src={linkedinProfileData?.profile_picture}
                            sx={{ 
                                    width: 60,
                                    height: 60,
                                    border: '1px solid rgba(0, 0, 0, 0.1)',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    mr: 2
                                  }}
                                >
                                  {!linkedinProfileData?.profile_picture && (
                                    <PersonIcon sx={{ fontSize: 40 }} />
                                  )}
                                </Avatar>
                                
                                <Box>
                                  <Typography variant="subtitle1" fontWeight={600}>
                                    {linkedinProfileData?.name || 'LinkedIn User'}
                          </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {linkedinProfileData?.headline || 'No headline available'}
                                  </Typography>
                                  
                                  {linkedinProfileData?.location && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', fontSize: '0.8rem' }}>
                                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5, fontSize: '0.9rem' }}>📍</Box>
                                      {linkedinProfileData.location}
                                    </Box>
                                  )}
                                </Box>
                        </Box>

                              <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid rgba(0, 0, 0, 0.03)' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                    Profile data from LinkedIn
                                  </Typography>
                                  {linkedinProfileData?.connection_count && (
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#0e76a8', fontWeight: 500 }}>
                                      {linkedinProfileData.connection_count} connections
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          )}
                          
                          {linkedinProfileSuccess && (
                            <Alert 
                              severity="success" 
                          sx={{
                                mt: 2, 
                                '& .MuiAlert-message': {
                                  fontWeight: 500
                                }
                              }}
                            >
                              {linkedinProfileSuccess}
                            </Alert>
                          )}
                          
                          {fetchingLinkedinProfile && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={30} />
                            </Box>
                          )}
                        </Box>

                        {/* Save Button */}
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={loading}
                          sx={{
                            mb: 2,
                            bgcolor: '#4890fe',
                            borderRadius: '8px',
                            py: 1.2,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              bgcolor: '#3a7ad9'
                            }
                          }}
                        >
                          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
                
                {/* Right column - Professional Details and LinkedIn */}
                <Box sx={{ 
                  flex: { xs: '1 1 100%', md: '0 0 500px' },  // Fixed width instead of flexible
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}>
                  {/* Professional Details Card */}
                  <Paper sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#2D3748' }}>
                      Professional Details
                    </Typography>
                    
                    <Box sx={{ 
                      width: '100%',
                      maxWidth: '400px',
                      mx: 'auto',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <TextField
                        fullWidth
                        label="Your Industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        placeholder="e.g. Technology, Marketing, Real Estate"
                        sx={{ mb: 3 }}
                      />
                      
                      <TextField
                        fullWidth
                        label="Your Role"
                        name="professionalRole"
                        value={formData.professionalRole}
                        onChange={handleChange}
                        placeholder="e.g. Founder / CEO, Marketing Manager"
                        sx={{ mb: 3 }}
                      />
                      
                      <TextField
                        fullWidth
                        label="Target Audience"
                        name="targetAudience"
                        value={formData.targetAudience}
                        onChange={handleChange}
                        placeholder="e.g. B2B Professionals, Local Communities"
                        sx={{ mb: 3 }}
                      />

                      {/* Timezone Section */}
                      <Box sx={{ mb: 3 }}>
                        <Autocomplete
                          options={commonTimezones.map(tz => ({
                            ...tz,
                            searchText: `${tz.label} ${tz.region} ${tz.value}`.toLowerCase()
                          }))}
                          value={commonTimezones.find(tz => tz.value === formData.timezone) || null}
                          onChange={(event, newValue) => {
                            setFormData(prev => ({ ...prev, timezone: newValue?.value || '' }));
                          }}
                          groupBy={(option) => option.region}
                          getOptionLabel={(option) => option.label}
                          filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;
                            const searchTerm = inputValue.toLowerCase();
                            return options.filter(option => 
                              option.searchText.includes(searchTerm)
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Timezone"
                              placeholder="Search timezone by country or city..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  '& fieldset': {
                                    borderColor: 'rgba(0, 0, 0, 0.1)',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#4890fe',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#4890fe',
                                  }
                                }
                              }}
                            />
                          )}
                          renderGroup={(params) => (
                            <li key={params.key}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 'bold',
                                  color: '#4890fe',
                                  px: 2,
                                  py: 1,
                                  bgcolor: alpha('#4890fe', 0.1),
                                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                                }}
                              >
                                {params.group}
                              </Typography>
                              <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                            </li>
                          )}
                          renderOption={(props, option) => (
                            <li {...props} style={{ paddingLeft: '24px' }}>
                              <Box sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {option.label}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          noOptionsText="No timezone found. Try searching by country or city name."
                          sx={{
                            '& .MuiAutocomplete-listbox': {
                              maxHeight: '300px',
                            },
                            '& .MuiAutocomplete-groupLabel': {
                              position: 'sticky',
                              top: 0,
                              zIndex: 1,
                            }
                          }}
                        />

                        {/* Show current time in selected timezone */}
                        {formData.timezone && (
                          <Box sx={{ 
                            mt: 2, 
                            p: 2, 
                            borderRadius: '6px', 
                            bgcolor: alpha('#4890fe', 0.1), 
                            textAlign: 'center' 
                          }}>
                            <Typography variant="body2" color="text.secondary">
                              Current time in your timezone:
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: '#4890fe' }}>
                              {new Date().toLocaleString('en-US', { 
                                timeZone: formData.timezone,
                                weekday: 'long',
                                hour: '2-digit', 
                                minute: '2-digit',
                                timeZoneName: 'short'
                              })}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Skills Section */}
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: '#2D3748' }}>
                        Skills
                      </Typography>

                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={customSkillInput}
                            onChange={(e) => setCustomSkillInput(e.target.value)}
                            placeholder="Add a skill"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                '& fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.1)',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#4890fe',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#4890fe',
                                }
                              }
                            }}
                          />
                          <Button
                            variant="contained"
                            onClick={handleAddCustomSkill}
                            disabled={!customSkillInput.trim()}
                            sx={{
                              bgcolor: '#4890fe',
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                bgcolor: '#3a7ad9'
                              }
                            }}
                          >
                            Add
                          </Button>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          {[...new Set([...selectedSkills, ...userAddedSkills])].map((skill) => (
                            <Chip
                              key={skill}
                              label={skill}
                              onClick={() => handleSkillSelect(skill)}
                              onDelete={() => handleSkillSelect(skill)}
                              color={selectedSkills.includes(skill) ? "primary" : "default"}
                              sx={{
                                borderRadius: '16px',
                                '&.MuiChip-colorPrimary': {
                                  bgcolor: '#4890fe',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: '#3a7ad9'
                                  }
                                }
                              }}
                            />
                          ))}
                        </Box>

                        {skillLimitError && (
                          <Alert 
                            severity="warning" 
                            sx={{ 
                              mb: 2,
                              '& .MuiAlert-message': {
                                fontWeight: 500
                              }
                            }}
                          >
                            You can select a maximum of 5 skills.
                          </Alert>
                        )}
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleProfessionalDetailsSubmit}
                        disabled={profDetailsLoading}
                        sx={{ 
                          mb: 2,
                          bgcolor: '#4890fe',
                          borderRadius: '8px',
                          py: 1.2,
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': {
                            bgcolor: '#3a7ad9'
                          }
                        }}
                      >
                        {profDetailsLoading ? <CircularProgress size={24} /> : 'Save Professional Details'}
                      </Button>

                      {profDetailsSuccess && (
                        <Alert 
                          severity="success" 
                          sx={{ 
                            mt: 2, 
                            mb: 2,
                            '& .MuiAlert-message': {
                              fontWeight: 500
                            }
                          }}
                        >
                          {profDetailsSuccess}
                        </Alert>
                      )}
                      
                      {/* Divider with text */}
                    <Box sx={{ 
                      display: 'flex',
                        alignItems: 'center', 
                        my: 3 
                      }}>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            px: 2, 
                            color: 'text.secondary',
                            fontWeight: 500
                          }}
                        >
                          Danger Zone
                          </Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                      </Box>
                          
                      {/* Logout Button */}
                          <Button
                            fullWidth
                        variant="outlined"
                        onClick={handleLogout}
                        startIcon={<Logout />}
                            sx={{ 
                          textTransform: 'none',
                              borderRadius: '8px',
                              py: 1.2,
                              fontWeight: 600,
                          mb: 2,
                          borderColor: 'rgba(239, 68, 68, 0.5)',
                          color: 'rgb(239, 68, 68)',
                              '&:hover': {
                            borderColor: 'rgba(239, 68, 68, 0.8)',
                            bgcolor: 'rgba(239, 68, 68, 0.04)'
                              }
                            }}
                          >
                        Logout
                          </Button>

                      {/* Delete Account Button */}
                            <Button
                              fullWidth
                              variant="outlined"
                        color="error"
                        onClick={() => setDeleteDialogOpen(true)}
                        startIcon={<DeleteOutline />}
                              sx={{ 
                                textTransform: 'none',
                          borderRadius: '8px',
                          py: 1.2,
                          fontWeight: 600,
                          borderColor: 'rgba(239, 68, 68, 0.5)',
                                '&:hover': {
                            borderColor: 'rgba(239, 68, 68, 0.8)',
                            bgcolor: 'rgba(239, 68, 68, 0.04)'
                          }
                        }}
                      >
                        Delete Account
                            </Button>
                    </Box>
                  </Paper>
                </Box>
              </Box>

              <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <DialogTitle sx={{ 
                  fontWeight: 600, 
                  color: '#2D3748',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 2
                }}>
                  Delete Account
                </DialogTitle>
                <DialogContent>
                  <DialogContentText sx={{ pt: 2 }}>
                    Are you sure you want to delete your account? This action cannot be undone. 
                    All your data, including posts, scheduled content, and settings will be permanently deleted.
                  </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                  <Button 
                    onClick={() => setDeleteDialogOpen(false)} 
                    disabled={deleteLoading}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 3,
                      color: '#4A5568',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteAccount}
                    color="error"
                    variant="contained"
                    disabled={deleteLoading}
                    sx={{ 
                      position: 'relative',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 3,
                      bgcolor: '#EF4444',
                      '&:hover': {
                        bgcolor: '#DC2626'
                      }
                    }}
                  >
                    {deleteLoading ? (
                      <CircularProgress 
                        size={24} 
                        sx={{ 
                          color: 'white',
                          position: 'absolute'
                        }} 
                      />
                    ) : 'Delete Account'}
                  </Button>
                </DialogActions>
              </Dialog>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default EditProfile; 